// Shared anonymous-tier transport for verification, public documents and rules FAQ.
//
// Core's graph routes run auth mode "enforce": even @public fields need a
// PRINCIPAL, and the anonymous tier's principal is the core-minted public
// token (GET <origin>/authnz/public/token → {access_token}, ~15min ES256).
// The main urql client sends the Keycloak bearer or nothing — nothing is
// refused — so this module carries its OWN cacheless client whose fetch
// mints (and caches, refreshing at 4/5 lifetime) the public token. Works
// identically for signed-in visitors: the public token is sufficient for
// @public fields and nothing else on this page needs more.
import { trackFetch } from './busy'
import { cacheExchange, createClient, fetchExchange, type Client } from '@urql/vue'
import { graphqlTracingExchange } from 'src/lib/tracing'
import { rateLimitExchange, rateLimitFetch } from 'src/lib/rateLimit'

const GRAPHQL_URL: string = import.meta.env.GRAPHQL_URL
// Lazy: in tests GRAPHQL_URL is a bare path ('/graphql'), which new URL()
// refuses at module-eval time; the token URL only matters when the real
// fetch path runs (never in tests — they inject their own client).
function tokenURL(): string {
  return new URL('/authnz/public/token', GRAPHQL_URL).toString()
}

let cached: { token: string; refreshAt: number } | null = null
let inFlight: Promise<string> | null = null

async function publicToken(): Promise<string> {
  if (cached && Date.now() < cached.refreshAt) return cached.token
  inFlight ??= (async () => {
    try {
      const res = await fetch(tokenURL(), { signal: AbortSignal.timeout(10_000) })
      if (!res.ok) throw new Error(`public token mint failed: ${res.status}`)
      const body = (await res.json()) as { access_token: string; expires_in?: number }
      const lifetimeMs = (body.expires_in ?? 900) * 1000
      cached = { token: body.access_token, refreshAt: Date.now() + (lifetimeMs * 4) / 5 }
      return body.access_token
    } finally {
      inFlight = null
    }
  })()
  return inFlight
}

/** Test seam: preload/clear the cached token. */
export function primePublicToken(token: string | null): void {
  cached = token ? { token, refreshAt: Date.now() + 60_000 } : null
}

const publicFetch: typeof fetch = async (input, init) => {
  const timeout = AbortSignal.timeout(10_000)
  const signal = init?.signal ? AbortSignal.any([init.signal, timeout]) : timeout
  const token = await publicToken()
  signal.throwIfAborted()
  const headers = new Headers(init?.headers)
  headers.set('Authorization', `Bearer ${token}`)
  return fetch(input, { ...init, headers, signal })
}

export function createPublicGraphqlClient(): Client {
  return createClient({
    url: GRAPHQL_URL,
    preferGetMethod: false,
    exchanges: [graphqlTracingExchange(), cacheExchange, rateLimitExchange(), fetchExchange],
    // trackFetch: the header's loading stripe (src/core/busy.ts).
    fetch: trackFetch(rateLimitFetch(publicFetch)),
  })
}
